import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import StepLabel from '@mui/material/StepLabel';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import api from '../api';
import { useNavigate, useLocation } from 'react-router-dom';

export default function ProgressSteps() {
    const [loading, setLoading] = useState(true);
    const containerRef = React.useRef(null);
    const [authenticated, setAuthenticated] = useState(false);
    // fixed height for the progress area (includes vertical padding)
    const FIXED_HEIGHT = 72; // px
    const [schoolProfileExists, setSchoolProfileExists] = useState(false);
    const [schoolProfileCompleted, setSchoolProfileCompleted] = useState(false);
    const [cigCompleted, setCigCompleted] = useState(false);
    const [ilgCompleted, setIlgCompleted] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();

    useEffect(() => {
        const fetchStatus = async () => {
            try {
                const me = await api.get('/api/public/me');
                if (me?.data?.authenticated) {
                    setAuthenticated(true);
                    try {
                        const pr = await api.get('/api/public/school-profile');
                        setSchoolProfileExists(Boolean(pr?.data?.exists));
                        setSchoolProfileCompleted(Boolean(pr?.data?.completed));
                        try {
                            const token = localStorage.getItem('token');
                            const cig = await api.get('/api/public/cig-applications', { headers: token ? { Authorization: `Bearer ${token}` } : {} });
                            if (cig?.data?.exists && cig?.data?.completed) setCigCompleted(true); else setCigCompleted(false);
                            const ilg = await api.get('/api/public/ilg-applications', { headers: token ? { Authorization: `Bearer ${token}` } : {} });
                            if (ilg?.data?.exists && ilg?.data?.completed) setIlgCompleted(true); else setIlgCompleted(false);
                        } catch (e) { /* ignore */ }
                    } catch (err) {
                        setSchoolProfileExists(false);
                        setSchoolProfileCompleted(false);
                    }
                } else {
                    setAuthenticated(false);
                }
            } catch (err) {
                setAuthenticated(false);
            } finally {
                setLoading(false);
            }
        };

        fetchStatus();

        const onFormsUpdated = () => {
            fetchStatus();
        };
        window.addEventListener('formsUpdated', onFormsUpdated);
        return () => window.removeEventListener('formsUpdated', onFormsUpdated);
    }, [location.pathname]);

    // debug: watch for height changes and state transitions
    React.useLayoutEffect(() => {
        const isReady = !loading && authenticated;
        if (containerRef.current) {
        }
        const contentEl = document.querySelector('main');
        if (contentEl) {
        }
    }, [loading, authenticated]);

    // debugging: log height whenever loading/isReady changes
    React.useLayoutEffect(() => {
        if (containerRef.current) {
        }
    }, [loading, authenticated]);

    const getActive = () => {
        const p = location.pathname.toLowerCase();
        if (p.startsWith('/schoolinfo')) return 0;
        if (p.startsWith('/grantlanding')) return 1;
        if (p.startsWith('/cigform') || p.startsWith('/ilgform')) return 2;
        return -1;
    };

    const finalLabel = (() => {
        const p = location.pathname.toLowerCase();
        // show specific grant name only when user is on that grant form
        if (p.startsWith('/cigform')) return 'Campus Improvement Grant';
        if (p.startsWith('/ilgform')) return 'Innovation Lounge Grant';
        // if both completed, show all-completed label
        if (cigCompleted && ilgCompleted) return 'All Grants Completed';
        // default when on grant landing or before selection
        return 'Select A Grant';
    })();

    const handleStepClick = (index) => {
        if (index === 0) return navigate('/schoolinfo');
        if (index === 1) {
            if (!schoolProfileExists) return;
            return navigate('/grantlanding');
        }
        if (index === 2) {
            if (!schoolProfileExists) return;
            return navigate(finalLabel.includes('ILG') ? '/ilgform' : '/cigform');
        }
    };

    // always render a container of fixed height (FIXED_HEIGHT).  The stepper may
    // be empty initially (while loading) but the surrounding box never changes
    // size, which prevents the gap from growing or shrinking during refresh.
    // we no longer condition on `authenticated` here.

    const p = location.pathname.toLowerCase();
    const showStepper = p.startsWith('/schoolinfo') || p.startsWith('/grantlanding') || p.startsWith('/cigform') || p.startsWith('/ilgform');
    const isReady = !loading && authenticated; // only display actual stepper after data is loaded

    const steps = ['School Information', 'Available Grants', finalLabel];

    return (
        <Box
            ref={containerRef}
            sx={{
                height: FIXED_HEIGHT,
                bgcolor: 'transparent',
                borderBottom: 'none',
                py: 2,
                position: 'relative',
                zIndex: 1100
            }}
        >
            <Stack spacing={1} sx={{ maxWidth: 'lg', mx: 'auto', px: 1 }}>
                {showStepper && isReady && (
                    <Stepper nonLinear activeStep={getActive()} sx={{ py: 0, backgroundColor: 'transparent' }}>
                        {steps.map((label, index) => (
                            <Step key={label} completed={(() => {
                                if (index === 0) return schoolProfileCompleted;
                                if (index === 1) return cigCompleted && ilgCompleted;
                                if (index === 2) {
                                    if (finalLabel === 'All Grants Completed') return true;
                                    if (finalLabel === 'Campus Improvement Grant') return cigCompleted;
                                    if (finalLabel === 'Innovation Lounge Grant') return ilgCompleted;
                                    return false;
                                }
                                return false;
                            })()}>
                                {(() => {
                                    const completed = (index === 0 && schoolProfileExists) || (index === 1 && cigCompleted && ilgCompleted) || (index === 2 && ((finalLabel === 'All Grants Completed') || (finalLabel === 'Campus Improvement Grant' && cigCompleted) || (finalLabel === 'Innovation Lounge Grant' && ilgCompleted)));
                                    const LockStepIcon = (props) => {
                                        const { completed, active, icon } = props;
                                        const size = 40;
                                        const completedBg = theme.palette.primary.main; // orange
                                        const incompleteBg = theme.palette.grey[500]; // solid grey
                                        const bgColor = completed ? completedBg : incompleteBg;
                                        const borderColor = bgColor; // no contrasting border
                                        const textColor = '#fff';
                                        return (
                                            <div style={{
                                                width: size,
                                                height: size,
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                backgroundColor: bgColor,
                                                border: `2px solid ${borderColor}`,
                                                boxSizing: 'border-box',
                                            }}>
                                                {completed ? <LockOutlinedIcon style={{ fontSize: 24, color: '#fff' }} /> : <span style={{ fontSize: 20, color: textColor }}>{icon}</span>}
                                            </div>
                                        );
                                    };

                                    return (
                                        <StepButton
                                            onClick={() => handleStepClick(index)}
                                            disabled={(index > 0 && !schoolProfileExists) || (index === 2 && (finalLabel === 'Select A Grant' || finalLabel === 'All Grants Completed' || (finalLabel === 'Campus Improvement Grant' && cigCompleted) || (finalLabel === 'Innovation Lounge Grant' && ilgCompleted)))}
                                            sx={{
                                                '&.Mui-disabled': { opacity: 0.6 },
                                                '& .MuiStepLabel-label': { color: index <= getActive() ? 'text.primary' : 'text.secondary', fontSize: '0.95rem' },
                                                '& .MuiStepIcon-root': { fontSize: 18, width: 32, height: 32 },
                                                backgroundColor: 'transparent'
                                            }}
                                        >
                                            <StepLabel StepIconComponent={LockStepIcon}>{label}</StepLabel>
                                        </StepButton>
                                    );
                                })()}
                            </Step>
                        ))}
                    </Stepper>
                )}
            </Stack>
        </Box>
    );
}
